import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Globe } from "lucide-react";

export const Route = createFileRoute("/admin/jazyky")({ component: LangPage });

const SUPPORTED = ["cs", "pl", "en", "es", "de", "pt", "fr", "hr", "sk", "ro", "it", "hu", "bg"] as const;
const REQUEST_THRESHOLD = 100;

const LANG_NAMES: Record<string, string> = {
  cs: "Čeština", pl: "Polština", en: "Angličtina", es: "Španělština",
  de: "Němčina", pt: "Portugalština", fr: "Francouzština", hr: "Chorvatština",
  sk: "Slovenština", uk: "Ukrajinština", ru: "Ruština", it: "Italština",
  nl: "Nizozemština", hu: "Maďarština", ro: "Rumunština", tr: "Turečtina",
  bg: "Bulharština",
  ja: "Japonština", ko: "Korejština", zh: "Čínština", ar: "Arabština",
};
const nameOf = (c: string) => LANG_NAMES[c] ?? c.toUpperCase();

function LangPage() {
  const { data = [], isLoading } = useQuery({
    queryKey: ["admin", "languages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("preferred_language");
      if (error) throw error;
      const counts = new Map<string, number>();
      for (const row of data ?? []) {
        const code = (row.preferred_language ?? "").toLowerCase();
        if (!code) continue;
        counts.set(code, (counts.get(code) ?? 0) + 1);
      }
      return Array.from(counts.entries())
        .map(([code, count]) => ({
          code,
          count,
          supported: (SUPPORTED as readonly string[]).includes(code),
        }))
        .sort((a, b) => b.count - a.count);
    },
  });

  const requests = useMemo(
    () => data.filter((r) => !r.supported && r.count >= REQUEST_THRESHOLD),
    [data]
  );
  const missing = useMemo(
    () => data.filter((r) => !r.supported),
    [data]
  );
  const supported = useMemo(
    () => data.filter((r) => r.supported),
    [data]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Globe className="h-5 w-5" />
        <h1 className="text-2xl font-semibold">Jazyky uživatelů</h1>
      </div>

      {requests.length > 0 && (
        <Card className="border-amber-500/50 bg-amber-50/40">
          <CardHeader className="flex flex-row items-center gap-2 space-y-0">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <CardTitle className="text-base">
              Požadavek na překlad ({requests.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            Tyto jazyky překročily {REQUEST_THRESHOLD} registrovaných uživatelů a zatím nejsou v systému dostupné:
            <ul className="mt-2 list-disc pl-5">
              {requests.map((r) => (
                <li key={r.code}>
                  <strong>{nameOf(r.code)}</strong> ({r.code}) — {r.count} uživatelů
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Jazyky bez překladu ({missing.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Načítání…</p>
          ) : missing.length === 0 ? (
            <p className="text-sm text-muted-foreground">Žádné neznámé jazyky.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Jazyk</TableHead>
                  <TableHead>Kód</TableHead>
                  <TableHead className="text-right">Uživatelé</TableHead>
                  <TableHead className="text-right">Stav</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {missing.map((r) => (
                  <TableRow key={r.code}>
                    <TableCell>{nameOf(r.code)}</TableCell>
                    <TableCell className="font-mono text-xs">{r.code}</TableCell>
                    <TableCell className="text-right">{r.count}</TableCell>
                    <TableCell className="text-right">
                      {r.count >= REQUEST_THRESHOLD ? (
                        <Badge className="bg-amber-500 hover:bg-amber-500">Přeložit</Badge>
                      ) : (
                        <Badge variant="outline">
                          {r.count}/{REQUEST_THRESHOLD}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Podporované jazyky</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Jazyk</TableHead>
                <TableHead>Kód</TableHead>
                <TableHead className="text-right">Uživatelé</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {SUPPORTED.map((code) => {
                const row = supported.find((r) => r.code === code);
                return (
                  <TableRow key={code}>
                    <TableCell>{nameOf(code)}</TableCell>
                    <TableCell className="font-mono text-xs">{code}</TableCell>
                    <TableCell className="text-right">{row?.count ?? 0}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
